import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { SectionFooter, settingsButtonSx, settingsText } from "../settings-section";
import { clientConfigService } from "../../utils/services/clientConfigService";
import { DEFAULT_RISK_BANDS, RISK_BY_KEY, riskDayRanges } from "../../utils/inventoryRiskPageDefaults";
import {
  INVENTORY_RISK_SETTINGS_KEY,
  RISK_BAND_LIMITS,
  RISK_BAND_ROWS,
  bandsFromRow,
  draftErrors,
  draftFromBands,
  draftToBands,
  draftToValue,
  sameBands,
} from "../../utils/inventoryRiskSettings";

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const cellSx = {
  fontFamily: "poppins",
  fontSize: 13,
  color: "var(--text-primary)",
  borderColor: "var(--border-color)",
  px: 1.25,
  py: 1,
  verticalAlign: "top",
};
const headSx = { ...cellSx, ...settingsText.muted, fontWeight: 600, py: 0.75 };

const daysFieldSx = {
  width: 120,
  "& .MuiInputBase-root": { fontFamily: "poppins", fontSize: 13.5 },
  "& .MuiInputBase-input": { py: 0.75 },
  "& .MuiFormHelperText-root": { fontFamily: "poppins", mx: 0 },
};

const CategoryName = ({ riskKey, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, pt: 0.75 }}>
    <Box
      sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: RISK_BY_KEY[riskKey].color, flexShrink: 0 }}
    />
    <Typography sx={{ ...settingsText.value, fontWeight: 600 }}>{children}</Typography>
  </Box>
);

const FixedRow = ({ riskKey, name, range, rule }) => (
  <TableRow>
    <TableCell sx={cellSx}>
      <CategoryName riskKey={riskKey}>{name}</CategoryName>
    </TableCell>
    <TableCell sx={{ ...cellSx, pt: 1.75 }}>{range}</TableCell>
    <TableCell sx={{ ...cellSx, pt: 1.75 }} colSpan={2}>
      <Typography sx={settingsText.muted}>{rule}</Typography>
    </TableCell>
  </TableRow>
);

/**
 * Stock risk categories for one client (`dashboard.inventory_risk` in
 * clientConfig): one row per category with its days-of-cover limit. The server
 * validates every save, only lets a session edit its own client (internal
 * admins: any client) and applies the limits to Inventory Risk, Purchase Order
 * Analysis and the Command Center.
 */
const InventoryRiskSettings = ({ clientId, onSaved, onCancel }) => {
  const [row, setRow] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const rows = await clientConfigService.fetch(clientId, 1, 0, INVENTORY_RISK_SETTINGS_KEY);
      const stored = (rows?.data || []).find((r) => r.key === INVENTORY_RISK_SETTINGS_KEY) || null;
      setRow(stored);
      setDraft(draftFromBands(bandsFromRow(stored)));
    } catch (error) {
      setLoadError(errorMessage(error, "Couldn't load the risk categories."));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <CircularProgress size={14} />
        <Typography sx={settingsText.muted}>Loading…</Typography>
      </Box>
    );
  }
  if (loadError) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <Typography sx={settingsText.muted}>{loadError}</Typography>
        <Button size="small" onClick={load} sx={settingsButtonSx}>
          Retry
        </Button>
      </Box>
    );
  }

  const errors = draftErrors(draft);
  const hasErrors = Object.keys(errors).length > 0;
  const bands = draftToBands(draft);
  // Ranges follow the draft once it is valid, so the table previews the result.
  const ranges = hasErrors ? null : riskDayRanges(bands);
  const unchanged = !hasErrors && sameBands(bands, bandsFromRow(row));
  const update = (key, patch) =>
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const value = draftToValue(draft);
      if (row?._id) {
        await clientConfigService.edit(row._id, { value, expected_updated_at: row.updatedAt });
      } else {
        await clientConfigService.create({ client_id: clientId, key: INVENTORY_RISK_SETTINGS_KEY, value });
      }
      toast.success("Risk categories saved.");
      onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't save the risk categories."));
    } finally {
      setSaving(false);
    }
  };

  const restoreStandard = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Restore the standard risk categories?")) return;
    setSaving(true);
    try {
      await clientConfigService.delete(row._id);
      toast.success("Standard risk categories restored.");
      onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't restore the standard categories."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-testid="inventory-risk-settings">
      <Typography sx={settingsText.muted} data-testid="inventory-risk-settings-status">
        {row
          ? `Customized · last saved ${new Date(row.updatedAt).toLocaleString()}${
              row.value?.updated_by?.email ? ` by ${row.value.updated_by.email}` : ""
            }`
          : "Using the standard risk categories."}
      </Typography>

      <Table size="small" sx={{ mt: 1.5 }} aria-label="Risk categories">
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>Category</TableCell>
            <TableCell sx={headSx}>Days of cover</TableCell>
            <TableCell sx={headSx}>Up to</TableCell>
            <TableCell sx={{ ...headSx, width: 60 }}>On</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <FixedRow riskKey="out_of_stock" name="Out of Stock" range="0" rule="Nothing on hand, still selling." />
          {RISK_BAND_ROWS.map((band) => {
            const field = draft[band.key];
            return (
              <TableRow key={band.key} sx={{ opacity: field.enabled ? 1 : 0.55 }}>
                <TableCell sx={cellSx}>
                  <CategoryName riskKey={band.key}>{band.name}</CategoryName>
                </TableCell>
                <TableCell sx={{ ...cellSx, pt: 1.75 }}>
                  {field.enabled ? (ranges?.[band.key] ?? "—") : "Off"}
                </TableCell>
                <TableCell sx={cellSx}>
                  <TextField
                    size="small"
                    value={field.days}
                    disabled={!field.enabled}
                    onChange={(event) => update(band.key, { days: event.target.value })}
                    error={Boolean(errors[band.key])}
                    helperText={errors[band.key] || `Standard: ${DEFAULT_RISK_BANDS[band.key]}`}
                    inputProps={{ inputMode: "numeric", "aria-label": `${band.name} up to days` }}
                    InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                    sx={daysFieldSx}
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  {band.required ? null : (
                    <Switch
                      size="small"
                      checked={field.enabled}
                      onChange={(event) => update(band.key, { enabled: event.target.checked })}
                      inputProps={{ "aria-label": `${band.name} on` }}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          <FixedRow
            riskKey="healthy"
            name="Healthy"
            range={ranges?.healthy ?? "—"}
            rule="More cover than the last category."
          />
        </TableBody>
      </Table>

      <Typography sx={{ ...settingsText.muted, fontSize: 11.5, mt: 1.5 }}>
        Days of cover = on hand ÷ average daily sales over the last 30 days. Each limit is
        inclusive, from {RISK_BAND_LIMITS.min} to {RISK_BAND_LIMITS.max} days. Critical and At Risk
        drive Revenue at Risk, &quot;Needs attention&quot; and the Command Center (Critical = act
        today, At Risk = this week). Applies to Inventory Risk, Purchase Order Analysis and the
        Command Center for this client.
      </Typography>

      <SectionFooter
        left={
          row && (
            <Button
              onClick={restoreStandard}
              disabled={saving}
              sx={{ ...settingsButtonSx, color: "var(--text-secondary)", px: 1 }}
            >
              Restore standard categories
            </Button>
          )
        }
      >
        <Button
          onClick={onCancel}
          disabled={saving}
          sx={{ ...settingsButtonSx, color: "var(--text-secondary)" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={hasErrors || saving || unchanged}
          onClick={save}
          sx={settingsButtonSx}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </SectionFooter>
    </Box>
  );
};

export default InventoryRiskSettings;
